import React, { useEffect, useState } from 'react';
import { api } from '@/utils/api';
import { useSubscription } from '@/hooks/useSubscription';
import { useTheme } from '@/hooks/useTheme';
import { uploadToCloudinary } from '@/utils/cloudinary';
import { compressImage } from '@/utils/imageCompression';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Building2, MapPin, Phone, Mail, User, Calendar, BadgeCheck,
  Edit2, Save, X, Users, Plus, Trash2, KeyRound, Shield, Upload, ImageIcon, Palette, Check,
} from 'lucide-react';
import { toast } from 'sonner';

export const Clinic = () => {
  const { plan, getAdminLimit } = useSubscription();
  const { themeId, setTheme, themes } = useTheme();
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ name: '', address: '', phone: '', image_url: '' });
  const [imageUploading, setImageUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  // Admin state
  const [admins, setAdmins] = useState([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [adminModal, setAdminModal] = useState(null); // null | 'create' | { ...admin }
  const [adminForm, setAdminForm] = useState({ full_name: '', email: '', password: '', role: 'admin' });
  const [adminSaving, setAdminSaving] = useState(false);
  const [deletingAdminId, setDeletingAdminId] = useState(null);

  useEffect(() => {
    fetchClinic();
  }, []);

  useEffect(() => {
    if (activeTab === 'admins') fetchAdmins();
  }, [activeTab]);

  const fetchClinic = async () => {
    try {
      const response = await api.get('/clinic');
      setClinic(response.data);
      setFormData({
        name: response.data.name || '',
        address: response.data.address || '',
        phone: response.data.phone || '',
        image_url: response.data.image_url || '',
      });
    } catch (error) {
      toast.error('Failed to load clinic details');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setFormData({
      name: clinic.name || '',
      address: clinic.address || '',
      phone: clinic.phone || '',
      image_url: clinic.image_url || '',
    });
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
  };

  // ── Admin helpers ────────────────────────────────────────────────────────
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageUploading(true);
    try {
      const compressed = await compressImage(file, { maxSizeMB: 2, maxWidthOrHeight: 1200 });
      const url = await uploadToCloudinary(compressed, { folder: 'clinic', clinicId: clinic?.id });
      setFormData((prev) => ({ ...prev, image_url: url }));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.message || 'Image upload failed');
    } finally {
      setImageUploading(false);
    }
  };

  // ── Admin helpers ────────────────────────────────────────────────────────
  const fetchAdmins = async () => {
    setAdminsLoading(true);
    try {
      const res = await api.get('/admins');
      setAdmins(res.data);
    } catch {
      toast.error('Failed to load admins');
    } finally {
      setAdminsLoading(false);
    }
  };

  const openCreateAdmin = () => {
    setAdminForm({ full_name: '', email: '', password: '', role: 'admin' });
    setAdminModal('create');
  };

  const openEditAdmin = (admin) => {
    setAdminForm({ full_name: admin.full_name, email: admin.email, password: '', role: admin.role || 'admin' });
    setAdminModal(admin);
  };

  const closeAdminModal = () => setAdminModal(null);

  const handleAdminSave = async (e) => {
    e.preventDefault();
    if (!adminForm.full_name.trim() || !adminForm.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    if (adminModal === 'create' && !adminForm.password) {
      toast.error('Password is required for new admins');
      return;
    }
    setAdminSaving(true);
    try {
      if (adminModal === 'create') {
        await api.post('/admins', adminForm);
        toast.success('Admin created successfully');
      } else {
        const payload = { full_name: adminForm.full_name, email: adminForm.email };
        if (adminForm.password) payload.password = adminForm.password;
        await api.put(`/admins/${adminModal.id}`, payload);
        toast.success('Admin updated successfully');
      }
      closeAdminModal();
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save admin');
    } finally {
      setAdminSaving(false);
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    setDeletingAdminId(adminId);
    try {
      await api.delete(`/admins/${adminId}`);
      toast.success('Admin deleted');
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete admin');
    } finally {
      setDeletingAdminId(null);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.address.trim()) {
      toast.error('Clinic name and address are required');
      return;
    }
    setSaving(true);
    try {
      const response = await api.put('/clinic', formData);
      setClinic(response.data);
      setEditing(false);
      toast.success('Clinic details updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update clinic');
    } finally {
      setSaving(false);
    }
  };

  const planBadgeColor = (plan) => {
    if (plan === 'basic') return 'bg-sage-100 text-sage-700';
    if (plan === 'pro') return 'bg-accent-100 text-accent-600';
    return 'bg-stone-100 text-stone-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-500">Loading clinic details...</div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-12" data-testid="clinic-page">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-sage-800 mb-2">Clinic Profile</h1>
          <p className="text-gray-600">View and manage your clinic information</p>
        </div>
        {activeTab === 'details' && !editing && (
          <Button
            onClick={handleEdit}
            className="bg-sage-500 hover:bg-sage-600 text-white rounded-full shadow-md gap-2"
          >
            <Edit2 className="w-4 h-4" />
            Edit Details
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-stone-200">
        <button
          onClick={() => setActiveTab('details')}
          className={`px-5 py-2.5 text-sm font-medium rounded-t-xl transition-colors ${
            activeTab === 'details'
              ? 'bg-white border border-b-white border-stone-200 text-sage-700 -mb-px'
              : 'text-gray-500 hover:text-sage-600'
          }`}
        >
          Clinic Details
        </button>
        <button
          onClick={() => setActiveTab('admins')}
          className={`px-5 py-2.5 text-sm font-medium rounded-t-xl transition-colors flex items-center gap-2 ${
            activeTab === 'admins'
              ? 'bg-white border border-b-white border-stone-200 text-sage-700 -mb-px'
              : 'text-gray-500 hover:text-sage-600'
          }`}
        >
          <Users className="w-4 h-4" />
          Admins
        </button>
        <button
          onClick={() => setActiveTab('appearance')}
          className={`px-5 py-2.5 text-sm font-medium rounded-t-xl transition-colors flex items-center gap-2 ${
            activeTab === 'appearance'
              ? 'bg-white border border-b-white border-stone-200 text-sage-700 -mb-px'
              : 'text-gray-500 hover:text-sage-600'
          }`}
        >
          <Palette className="w-4 h-4" />
          Appearance
        </button>
      </div>

      {activeTab === 'details' && (
        <div className="grid md:grid-cols-3 gap-6">
          {/* Main info card */}
          <div className="md:col-span-2">
            <Card className="bg-white rounded-3xl p-8 shadow-md border border-stone-100">
              {editing ? (
                <form onSubmit={handleSave} className="space-y-6">
                  <h2 className="text-xl font-heading font-semibold text-sage-800 mb-4">Edit Clinic Details</h2>

                  {/* Image upload */}
                  <div className="space-y-2">
                    <Label className="text-gray-700 font-medium">Clinic Image</Label>
                    <div className="flex items-center gap-4">
                      <div className="w-24 h-24 rounded-2xl border-2 border-dashed border-stone-200 flex items-center justify-center overflow-hidden bg-stone-50 flex-shrink-0">
                        {formData.image_url ? (
                          <img src={formData.image_url} alt="Clinic" className="w-full h-full object-cover" />
                        ) : (
                          <ImageIcon className="w-8 h-8 text-stone-300" />
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <label
                          htmlFor="clinic-image-upload"
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border border-stone-200 text-sm font-medium text-gray-600 cursor-pointer hover:bg-stone-50 transition-colors ${
                            imageUploading ? 'opacity-50 pointer-events-none' : ''
                          }`}
                        >
                          <Upload className="w-4 h-4" />
                          {imageUploading ? 'Uploading...' : 'Upload Image'}
                        </label>
                        <input
                          id="clinic-image-upload"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleImageUpload}
                          disabled={imageUploading}
                        />
                        {formData.image_url && (
                          <button
                            type="button"
                            onClick={() => setFormData((p) => ({ ...p, image_url: '' }))}
                            className="text-xs text-red-400 hover:text-red-600 text-left"
                          >
                            Remove image
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="clinic-name" className="text-gray-700 font-medium">
                      Clinic Name <span className="text-red-400">*</span>
                    </Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="clinic-name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Your clinic name"
                        className="pl-10 rounded-2xl border-stone-200 focus:border-sage-400"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="clinic-address" className="text-gray-700 font-medium">
                      Address <span className="text-red-400">*</span>
                    </Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="clinic-address"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        placeholder="Clinic address"
                        className="pl-10 rounded-2xl border-stone-200 focus:border-sage-400"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="clinic-phone" className="text-gray-700 font-medium">Phone</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="clinic-phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="Clinic phone number"
                        className="pl-10 rounded-2xl border-stone-200 focus:border-sage-400"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button
                      type="submit"
                      disabled={saving}
                      className="bg-sage-500 hover:bg-sage-600 text-white rounded-full px-6 gap-2"
                    >
                      <Save className="w-4 h-4" />
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCancel}
                      disabled={saving}
                      className="rounded-full px-6 gap-2 border-stone-200 text-gray-600"
                    >
                      <X className="w-4 h-4" />
                      Cancel
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="space-y-6">
                  <h2 className="text-xl font-heading font-semibold text-sage-800 mb-4">Clinic Details</h2>

                  {clinic?.image_url && (
                    <div className="w-full h-48 rounded-2xl overflow-hidden bg-stone-100 mb-2">
                      <img src={clinic.image_url} alt={clinic.name} className="w-full h-full object-cover" />
                    </div>
                  )}

                  <InfoRow icon={Building2} label="Clinic Name" value={clinic?.name} />
                  <InfoRow icon={MapPin} label="Address" value={clinic?.address} />
                  <InfoRow icon={Phone} label="Phone" value={clinic?.phone || '—'} />
                  <InfoRow icon={Mail} label="Email" value={clinic?.email} />
                  <InfoRow icon={User} label="Administrator" value={clinic?.admin_name} />
                  <InfoRow
                    icon={Calendar}
                    label="Member Since"
                    value={
                      clinic?.created_at
                        ? new Date(clinic.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })
                        : '—'
                    }
                  />
                </div>
              )}
            </Card>
          </div>

          {/* Subscription badge card */}
          <div className="md:col-span-1 space-y-4">
            <Card className="bg-white rounded-3xl p-6 shadow-md border border-stone-100">
              <h2 className="text-lg font-heading font-semibold text-sage-800 mb-4 flex items-center gap-2">
                <BadgeCheck className="w-5 h-5 text-sage-500" />
                Subscription
              </h2>
              <div
                className={`inline-block px-4 py-2 rounded-full text-sm font-semibold capitalize mb-3 ${planBadgeColor(plan)}`}
              >
                {plan || 'free'} Plan
              </div>
              <p className="text-sm text-gray-500">
                {plan === 'free'
                  ? 'Upgrade to unlock more owners, pets, and doctors.'
                  : 'Thank you for supporting VetCare!'}
              </p>
            </Card>
          </div>
        </div>
      )}

      {activeTab === 'admins' && (
        <AdminsTab
          admins={admins}
          loading={adminsLoading}
          adminLimit={getAdminLimit()}
          plan={plan}
          adminModal={adminModal}
          adminForm={adminForm}
          setAdminForm={setAdminForm}
          adminSaving={adminSaving}
          deletingAdminId={deletingAdminId}
          onOpenCreate={openCreateAdmin}
          onOpenEdit={openEditAdmin}
          onCloseModal={closeAdminModal}
          onSave={handleAdminSave}
          onDelete={handleDeleteAdmin}
        />
      )}

      {activeTab === 'appearance' && (
        <AppearanceTab themes={themes} themeId={themeId} onSelect={setTheme} />
      )}
    </div>
  );
};

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-4">
    <div className="mt-0.5 flex-shrink-0 w-9 h-9 bg-sage-50 rounded-xl flex items-center justify-center">
      <Icon className="w-4 h-4 text-sage-500" />
    </div>
    <div>
      <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">{label}</p>
      <p className="text-gray-800 font-medium">{value || '—'}</p>
    </div>
  </div>
);

const AdminsTab = ({
  admins, loading, adminLimit, plan, adminModal, adminForm, setAdminForm,
  adminSaving, deletingAdminId,
  onOpenCreate, onOpenEdit, onCloseModal, onSave, onDelete,
}) => {
  const limit = adminLimit;
  const atLimit = admins.length >= limit;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 text-sm text-gray-500">
            <Shield className="w-4 h-4 text-sage-500" />
            <span>
              {admins.length} / {limit} admins used
              <span className="ml-1.5 capitalize text-xs font-medium text-sage-600 bg-sage-50 px-2 py-0.5 rounded-full">
                {plan} plan
              </span>
            </span>
          </div>
        </div>
        <Button
          onClick={onOpenCreate}
          disabled={atLimit}
          className="bg-sage-500 hover:bg-sage-600 text-white rounded-full shadow-md gap-2 disabled:opacity-50"
          title={atLimit ? `Limit of ${limit} admins reached for ${plan} plan` : 'Add admin'}
        >
          <Plus className="w-4 h-4" />
          Add Admin
        </Button>
      </div>

      {atLimit && (
        <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-2xl text-sm text-amber-700">
          You've reached the admin limit for the <span className="font-semibold capitalize">{plan}</span> plan ({limit} admins).
          {plan === 'free' && ' Upgrade to Basic to add up to 5 admins.'}
        </div>
      )}

      {loading ? (
        <div className="text-center text-gray-400 py-12">Loading admins...</div>
      ) : admins.length === 0 ? (
        <div className="text-center text-gray-400 py-12">No admins found.</div>
      ) : (
        <div className="grid gap-3">
          {[...admins].sort((a, b) => new Date(a.created_at) - new Date(b.created_at)).map((admin, idx) => {
            const isFirstAdmin = idx === 0;
            return (
              <Card key={admin.id} className="bg-white rounded-2xl p-5 shadow-sm border border-stone-100 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-sage-100 flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-sage-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-gray-800 truncate">{admin.full_name}</p>
                    {isFirstAdmin && (
                      <span className="flex-shrink-0 text-xs font-medium bg-sage-100 text-sage-700 px-2 py-0.5 rounded-full">Owner</span>
                    )}
                    <span className={`flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded-full capitalize ${
                      admin.role === 'admin' ? 'bg-blue-50 text-blue-600' : 'bg-stone-100 text-stone-600'
                    }`}>{admin.role || 'admin'}</span>
                  </div>
                  <p className="text-sm text-gray-500 truncate">{admin.email}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Added {new Date(admin.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onOpenEdit(admin)}
                    className="rounded-full border-stone-200 text-gray-600 gap-1.5"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onDelete(admin.id)}
                    disabled={isFirstAdmin || deletingAdminId === admin.id}
                    title={isFirstAdmin ? 'Cannot delete the original clinic owner' : undefined}
                    className="rounded-full border-red-200 text-red-500 hover:bg-red-50 gap-1.5 disabled:opacity-40 disabled:pointer-events-none"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    {deletingAdminId === admin.id ? 'Deleting...' : 'Delete'}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create / Edit Modal */}
      {adminModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-heading font-semibold text-sage-800">
                {adminModal === 'create' ? 'Add Admin' : 'Edit Admin'}
              </h2>
              <button onClick={onCloseModal} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={onSave} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="admin-name" className="text-gray-700 font-medium">
                  Full Name <span className="text-red-400">*</span>
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="admin-name"
                    value={adminForm.full_name}
                    onChange={(e) => setAdminForm({ ...adminForm, full_name: e.target.value })}
                    placeholder="Admin full name"
                    className="pl-10 rounded-2xl border-stone-200 focus:border-sage-400"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="admin-email" className="text-gray-700 font-medium">
                  Email <span className="text-red-400">*</span>
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="admin-email"
                    type="email"
                    value={adminForm.email}
                    onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                    placeholder="admin@example.com"
                    className="pl-10 rounded-2xl border-stone-200 focus:border-sage-400"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="admin-password" className="text-gray-700 font-medium">
                  Password {adminModal === 'create' && <span className="text-red-400">*</span>}
                  {adminModal !== 'create' && <span className="text-gray-400 text-xs font-normal ml-1">(leave blank to keep current)</span>}
                </Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="admin-password"
                    type="password"
                    value={adminForm.password}
                    onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                    placeholder={adminModal === 'create' ? 'Create a password' : 'New password (optional)'}
                    className="pl-10 rounded-2xl border-stone-200 focus:border-sage-400"
                    required={adminModal === 'create'}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">Role <span className="text-red-400">*</span></Label>
                <Select
                  value={adminForm.role}
                  onValueChange={(val) => setAdminForm({ ...adminForm, role: val })}
                  disabled={adminModal !== 'create' && adminModal?.id === admins.find((_, i) => i === 0)?.id}
                >
                  <SelectTrigger className="rounded-2xl border-stone-200 focus:border-sage-400">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="submit"
                  disabled={adminSaving}
                  className="flex-1 bg-sage-500 hover:bg-sage-600 text-white rounded-full gap-2"
                >
                  <Save className="w-4 h-4" />
                  {adminSaving ? 'Saving...' : adminModal === 'create' ? 'Create Admin' : 'Save Changes'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCloseModal}
                  disabled={adminSaving}
                  className="rounded-full border-stone-200 text-gray-600 gap-2"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Appearance / Theme Switcher ──────────────────────────────────────────────

const AppearanceTab = ({ themes, themeId, onSelect }) => (
  <div className="max-w-3xl">
    <div className="mb-6">
      <h2 className="text-xl font-heading font-semibold text-sage-800 mb-1">Appearance</h2>
      <p className="text-sm text-gray-500">Choose a colour theme for your dashboard. The selection is saved per device.</p>
    </div>

    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
      {themes.map((theme) => {
        const active = theme.id === themeId;
        return (
          <button
            key={theme.id}
            onClick={() => onSelect(theme.id)}
            className={`group relative flex flex-col items-start gap-3 p-4 rounded-2xl border-2 transition-all text-left ${
              active
                ? 'border-sage-500 shadow-md bg-sage-50'
                : 'border-stone-200 hover:border-sage-300 hover:shadow-sm bg-white'
            }`}
          >
            {/* Swatch */}
            <div className="w-full h-14 rounded-xl overflow-hidden flex">
              <div className="flex-1 h-full" style={{ backgroundColor: theme.bgColor }} />
              <div className="w-10 h-full" style={{ backgroundColor: theme.primaryColor }} />
              <div className="w-5 h-full" style={{ backgroundColor: theme.sage[300] }} />
            </div>

            {/* Label */}
            <div>
              <p className="text-sm font-semibold text-gray-800 leading-tight">{theme.name}</p>
              <p className="text-xs text-gray-400 mt-0.5 leading-tight">{theme.description}</p>
            </div>

            {/* Active checkmark */}
            {active && (
              <span className="absolute top-2.5 right-2.5 w-5 h-5 rounded-full bg-sage-500 flex items-center justify-center shadow">
                <Check className="w-3 h-3 text-white" />
              </span>
            )}
          </button>
        );
      })}
    </div>

    <p className="mt-5 text-xs text-gray-400">
      Theme is applied instantly and persisted in your browser.
    </p>
  </div>
);

